import Store from 'store/store';
import { getShowtimesData } from 'utils/bolt';
import { getScreeningDetailsFormatted } from 'helpers/screeningDetails';
import QRCode from 'qrcode';
import DeviceDetector from 'device-detector-js';

const LINK_COLOR = '#0095D0';
const TEXT_COLOR = '#00061C';
const CONCESSION_TEXT_COLOR = '#08A7E1';
const MAX_WIDTH = 420;
const MAX_NAME_WIDTH = 260;
const MAX_EMAIL_WIDTH = 260;
const MAX_MOVIE_WIDTH = 310;
const MAX_NAME_WIDTH_FOR_TRUNCATION = 310;
const MAX_EMAIL_WIDTH_FOR_TRUNCATION = 310;
const MAX_MOVIE_WIDTH_FOR_TRUNCATION = 250;
const LEFTX = 82;

const THEATER_HEADER_TOP_Y = 156;
const THEATER_LOGO_WIDTH = 40;

const TICKET_QR_CODE_BORDER_OFFSET = 19.94;
const TICKET_QR_CODE_Y = THEATER_HEADER_TOP_Y + THEATER_LOGO_WIDTH + 21;
const TICKET_QR_CODE_WIDTH = 188;

const MOVIE_NAME_MID_Y = TICKET_QR_CODE_Y + 12;
const SCREENING_DETAILS_LEFT = LEFTX + TICKET_QR_CODE_WIDTH + 16;
const SCREENING_DETAILS_TOP_Y = MOVIE_NAME_MID_Y + 25;
const SCREENING_ICONS_WIDTH = 20;
const SCREENING_DETAILS_MID_Y = 1 + SCREENING_DETAILS_TOP_Y + SCREENING_ICONS_WIDTH / 2;
const SCREENING_DETAILS_TOP_Y_2 = SCREENING_DETAILS_TOP_Y + SCREENING_ICONS_WIDTH + 9;
const SCREENING_DETAILS_MID_Y_2 = 1 + SCREENING_DETAILS_TOP_Y_2 + SCREENING_ICONS_WIDTH / 2;
const NAME_TOP_Y = SCREENING_DETAILS_MID_Y_2 + 31;
const LEGAL_LINE_1_TOP_Y = 480;
const LEGAL_LINE_2_TOP_Y = LEGAL_LINE_1_TOP_Y + 37;
const LEGAL_LINE_3_TOP_Y = LEGAL_LINE_2_TOP_Y + 30;
const LEGAL_LINE_4_TOP_Y = LEGAL_LINE_3_TOP_Y + 17;
const LEGAL_LINE_5_TOP_Y = LEGAL_LINE_4_TOP_Y + 17;

const CONCESSION_LINE_1_TOP_Y = 670;
const CONCESSION_LINE_2_TOP_Y = CONCESSION_LINE_1_TOP_Y + 30;
const CONCESSION_ICONS_WIDTH = 20;
const CONCESSION_ICONS_MID = CONCESSION_LINE_2_TOP_Y + CONCESSION_ICONS_WIDTH / 2;
const CONCESSION_LINE_3_TOP_Y = CONCESSION_LINE_2_TOP_Y + 33;
const CONCESSION_QRCODE_WIDTH = 133;

const scaleFactor = 2.8;

const loadImg = async (src) => {
	const image = await new Promise((resolve, reject) => {
		const image = document.createElement('img');
		image.crossOrigin = 'anonymous';
		image.src = src + '?_cors=1';
		image.onload = () => resolve(image);
		image.onerror = reject;
	});
	return image;
};

const formatTheaterDetail = (detail) => {
	return detail ? detail + ', ' : '';
};

const wrapText = (ctx, text, x, y, maxWidth, lineHeight) => {
	if (!text) return;
	let words = text.split(' ');
	let line = '';
	let testLine = '';
	let lineArray = [];

	for (let i = 0; i < words.length; i++) {
		testLine += `${words[i]} `;
		let metrics = ctx.measureText(testLine);
		let testWidth = metrics.width;
		if (testWidth > maxWidth && i > 0) {
			lineArray.push([line, x, y]);
			y += lineHeight;
			line = `${words[i]} `;
			testLine = `${words[i]} `;
		}
		else {
			line += `${words[i]} `;
		}
		if (i === words.length - 1) {
			lineArray.push([line, x, y]);
		}
	}

	lineArray.forEach(line => {
		ctx.fillText(line[0], line[1], line[2]);
	});
};


const generateQRCode = async (data) => {
	try {
		return await QRCode.toCanvas(data);
	} catch (error) {
		console.error('Error generating QR code:', error);
	}
};

const stringEllipsis = (ctx, text, maxWidth) => {
	let width = ctx.measureText(text).width;
	let truncatedText = '';
	let ellipsis = '...';
	let ellipsisWidth = ctx.measureText(ellipsis).width;
	if (width <= maxWidth || width <= ellipsisWidth) {
		return text;
	} else {
		for (let i = 0; i < text.length; i++) {
			truncatedText = text.substring(0, i) + ellipsis;
			const truncatedWidth = ctx.measureText(truncatedText).width;
			if (truncatedWidth > maxWidth) {
				break;
			}
		}
	}
	return truncatedText;
};

export const generateAndDownloadPdf = async () => {
	if (!window.jspdf) return;
	Store.emit('SHOW_FULL_LOADER', true);
	const { jsPDF } = window.jspdf;

	const store = Store.get() || {};

	const ticketData = store?.showtimes?.customer?.tickets || [];
	const ticket = ticketData[0];
	const theaterData = ticket?.theater || {};
	const appData = store?.appData || {};
	const sharedImageRoot = store.appData?.urls?.roots?.sharedImages || '';
	const studioRoot = appData?.urls?.roots?.studio || '';
	const defaultFont = 'proxima-nova';
	const logoShade = 'light';
	const logo = ticket?.theater?.chain?.logo || 'default.png';
	const logoLink = `${sharedImageRoot}/logos/${logoShade}/${logo}`;

	const baseImagePath = `${studioRoot}/images/ticket/ticket-download-latest-2.svg`;

	const baseImage = await loadImg(baseImagePath);
	const theaterLogo = await loadImg(logoLink);
	const calendarIcon = await loadImg(`${studioRoot}/images/ticket/calendar.svg`);
	const clockIcon = await loadImg(`${studioRoot}/images/ticket/clock.svg`);
	const reelIcon = await loadImg(`${studioRoot}/images/ticket/reel.svg`);
	const ticketIcon = await loadImg(`${studioRoot}/images/ticket/ticket.svg`);
	const concessionIcons = await loadImg(`${studioRoot}/images/ticket/concessionIcons.svg`);
	let movieName = store?.appData?.meta?.title?.locale || '';
	let theaterName = theaterData.name || '';

	const pdf = new jsPDF('p', 'mm', 'a4');
	pdf.setFont('Helvetica', 'normal', 400);
	pdf.setFontSize(12);
	pdf.setTextColor(LINK_COLOR);
	const c = document.createElement('canvas');
	const docWidth = pdf.internal.pageSize.getWidth();
	const docHeight = pdf.internal.pageSize.getHeight();
	const context = c.getContext('2d');
	c.width = baseImage.naturalWidth * scaleFactor;
	c.height = baseImage.naturalHeight * scaleFactor;
	context.scale(scaleFactor, scaleFactor);
	const deviceDetector = new DeviceDetector();
	const userAgent = window.navigator.userAgent;
	const device = deviceDetector.parse(userAgent);
	const isSafari = device.client.name.toLowerCase().includes('safari');

	const showtimesAppData = getShowtimesData();
	const concessionVouchers = showtimesAppData.concessionVouchers || {};

	await ticketData.reduce(async (accum, ticket, index) => {
		await accum;
		if (index) {
			pdf.addPage();
		}
		context.clearRect(0, 0, c.width, c.height);
		context.drawImage(baseImage, 0, 0);

		//Theater logo
		const logoWidth = theaterLogo.naturalWidth;
		const logoHeight = theaterLogo.naturalHeight;
		const renderedLogoHeight = logoHeight * (THEATER_LOGO_WIDTH / logoWidth);
		const desiredLogoMidPoint = THEATER_HEADER_TOP_Y + (THEATER_LOGO_WIDTH / 2);
		const logoTopY = desiredLogoMidPoint - (renderedLogoHeight / 2);
		context.drawImage(theaterLogo, LEFTX, logoTopY, THEATER_LOGO_WIDTH, renderedLogoHeight);

		//Theater name
		context.font = `700 16px ${defaultFont}, sans-serif`;
		context.textBaseline = 'top';
		context.fillStyle = TEXT_COLOR;
		context.textAlign = 'left';
		context.fillText(theaterName, LEFTX + THEATER_LOGO_WIDTH + 11, THEATER_HEADER_TOP_Y + 3);

		//Theater Address
		context.font = `400 12px ${defaultFont}, sans-serif`;
		const street = formatTheaterDetail(theaterData.street);
		const city = formatTheaterDetail(theaterData.city);
		const state = theaterData.state ? theaterData.state + ' ' : '';
		const postcode = theaterData.postcode;
		const fullAddress = street + city + state + postcode;
		context.fillText(fullAddress, LEFTX + THEATER_LOGO_WIDTH + 11, THEATER_HEADER_TOP_Y + 22);

		// QR code for ticket scanning
		const vanityUrl = store?.appData?.meta?.url || '';
		const ticketQRString = `${vanityUrl}${vanityUrl.endsWith('/') ? '' : '/'}#ticket=${ticket?.shortId || ticket?.id}`;
		const ticketQR = await generateQRCode(ticketQRString);
		context.drawImage(
			ticketQR,
			LEFTX - TICKET_QR_CODE_BORDER_OFFSET,
			TICKET_QR_CODE_Y - TICKET_QR_CODE_BORDER_OFFSET,
			TICKET_QR_CODE_WIDTH + 2 * TICKET_QR_CODE_BORDER_OFFSET,
			TICKET_QR_CODE_WIDTH + 2 * TICKET_QR_CODE_BORDER_OFFSET
		);
		const ticketNumText = `Ticket No: ${ticket?.shortId || ticket?.id}`;
		context.font = `500 10px ${defaultFont}, sans-serif`;
		context.textAlign = 'center';
		context.fillText(ticketNumText, LEFTX + TICKET_QR_CODE_WIDTH / 2, TICKET_QR_CODE_Y + TICKET_QR_CODE_WIDTH + 7);

		//Movie Title
		const movieNameFontSize = showtimesAppData?.pdfTickets?.movieNameFontSize || '24px'; // 28
		context.font = `700 ${movieNameFontSize} ${defaultFont}, sans-serif`;
		context.textBaseline = 'middle';
		context.textAlign = 'left';
		context.fillText(stringEllipsis(context, movieName, MAX_MOVIE_WIDTH_FOR_TRUNCATION), SCREENING_DETAILS_LEFT, MOVIE_NAME_MID_Y, MAX_MOVIE_WIDTH);

		let screeningFormat = ticket?.screening?.format;
		if (screeningFormat === '2D') {
			screeningFormat = 'ENG';
		}
		if (screeningFormat === '2D-SUBBED') {
			screeningFormat = 'ES SUB';
		}

		const formatted = getScreeningDetailsFormatted({
			date: ticket?.screening?.date || '',
			time: ticket?.screening?.time || '',
			format: screeningFormat
		}, getShowtimesData()?.clockFormat);

		//Screening Info
		context.drawImage(calendarIcon, SCREENING_DETAILS_LEFT, SCREENING_DETAILS_TOP_Y, SCREENING_ICONS_WIDTH, SCREENING_ICONS_WIDTH);
		context.font = `700 16px ${defaultFont}, sans-serif`;
		const dateX = SCREENING_DETAILS_LEFT + SCREENING_ICONS_WIDTH + 8;
		context.fillText(formatted.date, dateX, SCREENING_DETAILS_MID_Y);
		const clockX = dateX + context.measureText(formatted.date).width + 16;
		context.drawImage(clockIcon, clockX, SCREENING_DETAILS_TOP_Y, SCREENING_ICONS_WIDTH, SCREENING_ICONS_WIDTH);
		const timeX = clockX + SCREENING_ICONS_WIDTH + 8;
		context.fillText(formatted.time, timeX, SCREENING_DETAILS_MID_Y);
		context.drawImage(reelIcon, SCREENING_DETAILS_LEFT, SCREENING_DETAILS_TOP_Y_2, SCREENING_ICONS_WIDTH, SCREENING_ICONS_WIDTH);
		const formatX = SCREENING_DETAILS_LEFT + SCREENING_ICONS_WIDTH + 8;
		context.fillText(formatted.format, formatX, SCREENING_DETAILS_MID_Y_2);
		const ticketX = formatX + context.measureText(formatted.format).width + 16;
		context.drawImage(ticketIcon, ticketX, SCREENING_DETAILS_TOP_Y_2, SCREENING_ICONS_WIDTH, SCREENING_ICONS_WIDTH);
		const numTicketsX = ticketX + SCREENING_ICONS_WIDTH + 8;
		context.fillText(`${index + 1} of ${ticketData.length}`, numTicketsX, SCREENING_DETAILS_MID_Y_2);

		//Name
		context.textBaseline = 'top';
		const copy = store?.appData?.copy?.baseCopy || {};
		context.font = `400 14px ${defaultFont}, sans-serif`;
		context.fillText(copy.$NAME, SCREENING_DETAILS_LEFT, NAME_TOP_Y);
		context.font = `700 16px ${defaultFont}, sans-serif`;
		let userFullName = ticket.userFullName;
		if (index > 0) {
			userFullName = `${userFullName} (Guest #${index})`;
		}
		context.fillText(stringEllipsis(context, userFullName, MAX_NAME_WIDTH_FOR_TRUNCATION), SCREENING_DETAILS_LEFT, NAME_TOP_Y + 22, MAX_NAME_WIDTH);
		//Email
		context.font = `400 14px ${defaultFont}, sans-serif`;
		context.fillText(copy.$EMAIL, SCREENING_DETAILS_LEFT, NAME_TOP_Y + 22 + 22);
		context.font = `700 16px ${defaultFont}, sans-serif`;
		context.fillText(stringEllipsis(context, ticket.userEmail, MAX_EMAIL_WIDTH_FOR_TRUNCATION), SCREENING_DETAILS_LEFT, NAME_TOP_Y + 22 + 22 + 22, MAX_EMAIL_WIDTH);

		//Legal Copy
		context.font = `400 9px ${defaultFont}, sans-serif`;
		wrapText(context, copy.$TICKET_PDF_LEGAL_1, LEFTX, LEGAL_LINE_1_TOP_Y, MAX_WIDTH, 11);
		wrapText(context, copy.$TICKET_PDF_LEGAL_2, LEFTX, LEGAL_LINE_2_TOP_Y, MAX_WIDTH, 11);
		wrapText(context, copy.$TICKET_PDF_LEGAL_3, LEFTX, LEGAL_LINE_3_TOP_Y, MAX_WIDTH, 11);
		wrapText(context, copy.$TICKET_PDF_LEGAL_4, LEFTX, LEGAL_LINE_4_TOP_Y, MAX_WIDTH, 11);
		wrapText(context, copy.$TICKET_PDF_LEGAL_5, LEFTX, LEGAL_LINE_5_TOP_Y, MAX_WIDTH, 11);
		//Concession QR Code section
		// CHANGE FOR REACHER -- ALL TICKETS HAVE CONCESSIONS //
		const chain = ticket?.theater?.chain;
		const concessionVoucher = concessionVouchers[chain.id] || concessionVouchers.other;
		const concessionTitle = concessionVoucher?.title;
		const concessionSubtitle = concessionVoucher?.subtitle;
		const concessionDescription = concessionVoucher?.description;
		// For Reacher!!
		const hasLongConcession = concessionDescription.length > 350;
		context.font = `700 15px ${defaultFont}, sans-serif`;
		context.setLineDash([6]);
		context.strokeRect(LEFTX - 48, CONCESSION_LINE_1_TOP_Y - 11, 520, 0);
		context.fillStyle = CONCESSION_TEXT_COLOR;
		context.fillText(concessionTitle, LEFTX, CONCESSION_LINE_1_TOP_Y + 4);
		context.drawImage(concessionIcons, LEFTX, hasLongConcession ? CONCESSION_LINE_2_TOP_Y - 3 : CONCESSION_LINE_2_TOP_Y, 30, CONCESSION_ICONS_WIDTH);
		context.font = `400 10px ${defaultFont}, sans-serif`;
		context.fillStyle = TEXT_COLOR;
		context.fillText(concessionSubtitle, LEFTX + 30, hasLongConcession ? CONCESSION_ICONS_MID - 3 : CONCESSION_ICONS_MID);
		wrapText(context, concessionDescription, LEFTX, hasLongConcession ? CONCESSION_LINE_3_TOP_Y - 3 : CONCESSION_LINE_3_TOP_Y, MAX_WIDTH, 13);

		pdf.addImage(c.toDataURL('image/jpeg'), 'JPEG', 0, 0, docWidth, docHeight);
		pdf.textWithLink(copy.$PDF_PRIVACY || 'Privacy Notice', 29, 225, { url: 'https://studios.amazon.com/help/privacy-notice' });
		pdf.textWithLink(copy.$PDF_CONDITIONS_OF_USE || 'Conditions of Use', 110, 225, { url: 'https://studios.amazon.com/help/conditions-of-use' });

		// test - show canvas on screen
		// const ticketAreaDiv = document.querySelector("div[class^='ticketArea']");
		// ticketAreaDiv.appendChild(c);
	}, '');

	pdf.save(`Prime-Premiere-Passes-${movieName.replace(/\W+/g, '-')}.pdf`);
	Store.emit('SHOW_FULL_LOADER', false);
	Store.emit('TRACK_DOWNLOAD_TICKET', ({ theaterName, numTickets: ticketData.length }));
};
